import React from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Form } from "~/components/ui/form";
import AddressFields from "~/pages/clients/components/client-detail/address/form/AddressFields";
import DefaultAddressCheckbox from "~/pages/clients/components/client-detail/address/form/DefaultAddressCheckbox";
import RegionComunaSelector from "~/pages/clients/components/client-detail/address/form/RegionComunaSelector";

export interface AddressFormValues {
  street: string;
  number: string;
  complement?: string;
  city: string;
  state: string;
  comuna: string;
  isDefault: boolean;
}

interface AddressFormProps {
  defaultValues?: AddressFormValues;
  onSubmit: (values: AddressFormValues) => void;
  selectedRegion: string;
  setSelectedRegion: (region: string) => void;
  selectedComuna: string;
  setSelectedComuna: (comuna: string) => void;
  availableComunas: { id: string; name: string }[];
  isEdit?: boolean;
  clientId?: string;
  onSuccess?: () => void;
}

// Add Zod schema for required fields
const addressFormSchema = z.object({
  street: z.string().min(1, "La calle es obligatoria"),
  number: z.string().min(1, "El número es obligatorio"),
  complement: z.string().optional(),
  city: z.string().min(1, "La ciudad es obligatoria"),
  state: z.string().optional(),
  comuna: z.string().optional(),
  isDefault: z.boolean(),
});

const AddressForm: React.FC<AddressFormProps> = ({
  defaultValues,
  onSubmit,
  selectedRegion,
  setSelectedRegion,
  selectedComuna,
  setSelectedComuna,
  availableComunas = [], // Add default empty array to prevent map errors
  isEdit = false,
  onSuccess,
}) => {
  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: defaultValues || {
      street: "",
      number: "",
      complement: "",
      city: "",
      state: "",
      comuna: "",
      isDefault: false,
    },
  });

  const handleSubmit = async (values: AddressFormValues) => {
    // Validate that region and comuna are selected
    if (!selectedRegion) {
      console.error("Region is required but not selected");
      return;
    }

    if (!selectedComuna) {
      console.error("Comuna is required but not selected");
      return;
    }

    // Include the region and comuna IDs in the form data
    const formDataWithLocation = {
      ...values,
      regionId: selectedRegion,
      comunaId: selectedComuna,
    };

    await onSubmit(formDataWithLocation);
    if (onSuccess) {
      onSuccess();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <AddressFields control={form.control} />

        <RegionComunaSelector
          selectedRegion={selectedRegion}
          setSelectedRegion={setSelectedRegion}
          selectedComuna={selectedComuna}
          setSelectedComuna={setSelectedComuna}
          availableComunas={availableComunas}
        />

        <DefaultAddressCheckbox control={form.control} />

        <div className="flex justify-end">
          <Button
            type="submit"
            onClick={() => console.log("Submit button clicked")}
          >
            {isEdit ? "Actualizar dirección" : "Guardar dirección"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddressForm;
